import { pushRoute } from '../../lib/router.js';
import useFormInitialValues from '../../hooks/useFormInitialValues.js';

import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { Col, Grid, Row } from 'react-bootstrap';
import { getOrgTermsContent } from '../../content/utils.js';
import { mapFinalFormErrors, momentToDateString } from '../../lib/utils.js';
import {
  retrievePlanStatus,
  retrieveProject,
  retrieveProjects,
} from '../../modules/projects.js';
import { retrieveProjectOwners } from '../../modules/projectOwners.js';
import SubHeader from '../../components/SubHeader.jsx';
import { searchUsers, selectSearchUsersResults } from '../../modules/users.js';
import {
  retrieveProjectStatuses,
  selectProjectStatusesWithArchived,
} from '../../modules/projectStatuses.js';
import {
  createParentPortfolio,
  retrieveParentPortfolio,
  retrievePortfolios,
  selectParentPortfolio,
  selectUnassignedPortfolios,
  updateParentPortfolio,
} from '../../modules/portfolio.js';
import ParentPortfolioForm from '../../components/parent-portfolio/ParentPortfolioForm.jsx';
import PortfolioHeader from '../../components/headers/PageHeaders/Portfolio/PortfolioHeader.jsx';

const mapErrors = mapFinalFormErrors('Failed to save parent portfolio');

const defaultValues = {
  start_date: null,
  end_date: null,
};

const mapStateToProps = (state, ownProps) =>
  createSelector(
    selectParentPortfolio(ownProps.parentPortfolioId),
    (parentPortfolio) => {
      const unassignedPortfolios = selectUnassignedPortfolios(state);
      const assignedPortfolios =
        parentPortfolio && parentPortfolio.portfolios
          ? parentPortfolio.portfolios
          : [];
      const allAvailablePortfolios = [
        ...assignedPortfolios,
        ...unassignedPortfolios,
      ];

      return {
        parentPortfolio,
        editing: typeof ownProps.parentPortfolioId !== 'undefined',
        parentPortfolioId: ownProps.parentPortfolioId,
        portfolios: allAvailablePortfolios,
        customProjectStatuses: selectProjectStatusesWithArchived(state),
        searchResults: selectSearchUsersResults(state),
      };
    }
  )(state);

const mapDispatchToProps = {
  createParentPortfolio,
  updateParentPortfolio,
  retrieveParentPortfolio,
  retrieveProject,
  retrieveProjects,
  retrievePortfolios,
  retrieveProjectOwners,
  retrievePlanStatus,
  searchUsers,
  retrieveProjectStatuses,
};

const ParentPortfolioFormContainer = (props) => {
  const {
    editing,
    parentPortfolioId,
    parentPortfolio,
    portfolios,
    retrievePortfolios,
    createParentPortfolio,
    updateParentPortfolio,
    retrievePlanStatus,
    customProjectStatuses,
    retrieveProjectStatuses,
  } = props;
  const [selectedPortfolios, updateSelectedPortfolios] = useState([]);
  const [planStatus, setPlanStatus] = useState(true);
  const formResource = parentPortfolio
    ? {
        ...parentPortfolio,
        project_status_id: `${parentPortfolio.project_status_id}`,
      }
    : null;

  const initialValues = useFormInitialValues(formResource, defaultValues);

  const onSubmit = (values) => {
    const resource = {
      name: values.name,
      start_date: values.start_date
        ? momentToDateString(values.start_date)
        : null,
      end_date: values.end_date ? momentToDateString(values.end_date) : null,
      project_status_id: values.project_status_id,
      portfolioIds: selectedPortfolios.map((p) => p.id),
    };

    const submitPromise = editing
      ? updateParentPortfolio(parentPortfolioId, resource)
      : createParentPortfolio(resource);

    return submitPromise
      .then(() => {
        retrievePortfolios().then(() => pushRoute('projects/index'));
      })
      .catch(mapErrors);
  };

  const validate = (values) => {
    const errors = {};

    if (!values.name) {
      errors.name = 'Title is required';
    }

    if (!values.start_date) {
      errors.start_date = 'Start Date is required';
    }

    if (!values.end_date) {
      errors.end_date = 'End Date is required';
    }

    if (!values.project_status_id) {
      errors.project_status_id = getOrgTermsContent('requiredProjectStatus');
    }

    return errors;
  };

  useEffect(() => {
    retrievePortfolios();

    if (!editing) {
      retrievePlanStatus().then(({ canCreateProject }) => {
        setPlanStatus(canCreateProject);
      });
    }
  }, []);

  useEffect(() => {
    retrieveProjectStatuses();
  }, []);

  useEffect(() => {
    if (editing && parentPortfolio && parentPortfolio.portfolios) {
      updateSelectedPortfolios(
        parentPortfolio.portfolios.map((p) => ({ id: p.id }))
      );
    }
  }, [parentPortfolio]);

  return (
    <React.Fragment>
      <PortfolioHeader portfolio={parentPortfolio || {}} />
      <SubHeader flexJustify small>
        <h2>
          {editing
            ? `Edit ${getOrgTermsContent('parentPortfolio')}`
            : `Add ${getOrgTermsContent('parentPortfolio')}`}
        </h2>
      </SubHeader>
      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  <Form
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    editing={editing}
                    projectStatuses={customProjectStatuses}
                    portfolios={portfolios}
                    selectedPortfolios={selectedPortfolios}
                    updateSelectedPortfolios={updateSelectedPortfolios}
                    component={ParentPortfolioForm}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ParentPortfolioFormContainer);

ParentPortfolioFormContainer.propTypes = {
  parentPortfolioId: PropTypes.number,
};
